import { ref } from 'vue';
import { isAxiosError } from 'axios';
import { useBulkDelete } from '@/shared/lib';
import { deleteRejectedRecord, deleteRecords } from '../api/records.api';
import type { RejectedItem } from './models.types';

export interface RejectedActionsOptions {
  refresh: () => Promise<void>;
  selectedIds: () => number[];
  removeFromSelection: (ids: number[]) => void;
}

export function useRejectedActions(options: RejectedActionsOptions) {
  const pendingIds = ref<number[]>([]);

  const refreshAfterDeletion = () => {
    options.refresh().catch((error: unknown) => {
      console.error('Erro ao atualizar a listagem após a exclusão:', error);
    });
  };

  // A revision sent back to approval between the modal and the confirmation is refused with 400:
  // the row no longer belongs to this list.
  const deleteRejectedRefreshingOnRefusal = async (id: number) => {
    try {
      await deleteRejectedRecord(id);
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response?.status === 400) refreshAfterDeletion();
      throw error;
    }
  };

  const deleteRejectedListRefreshingOnRefusal = async (ids: number[]) => {
    const response = await deleteRecords(ids);
    if (response.data.successCount === 0 && response.data.failureCount > 0) {
      refreshAfterDeletion();
    }
    return response;
  };

  const { openDeleteConfirmationModal } = useBulkDelete<{ id: number }>({
    entityName: { singular: 'Registro', plural: 'Registros' },
    getConfirmTitle: (count) =>
      count <= 1
        ? 'Tem certeza que deseja deletar o Registro?'
        : 'Tem certeza que deseja deletar os Registros?',
    successMessage: 'Registro deletado com sucesso.',
    singleDeleteApi: deleteRejectedRefreshingOnRefusal,
    bulkDeleteApi: deleteRejectedListRefreshingOnRefusal,
    onDeleteSuccess: refreshAfterDeletion,
    onStaleData: refreshAfterDeletion,
    removeFromSelection: options.removeFromSelection,
    selectedCount: () => pendingIds.value.length,
    selectedIds: () => pendingIds.value,
  });

  const openDeleteConfirmation = async (ids: number[]) => {
    if (ids.length === 0) return;
    pendingIds.value = ids;
    await openDeleteConfirmationModal();
  };

  const openSingleDeleteConfirmation = (rejected: RejectedItem) =>
    openDeleteConfirmation([rejected.id]);

  const openBulkDeleteConfirmation = () => openDeleteConfirmation(options.selectedIds());

  return { openSingleDeleteConfirmation, openBulkDeleteConfirmation };
}
